import React from 'react';
import { GetApp, Add, CloseSharp, RateReview, Settings, Refresh, ContentCopy, Search as SearchIcon, NotInterested as NotInterestedIcon } from '@mui/icons-material';
import ButtonComponent from '../../../component/button/button.component';
import CardFrom, { CardFormConfig } from '../../../component/card/card-from';
import i18n from '../../../i18n';
import { RestaurantInfoState } from './restaurant-info-state';
import { observer } from 'mobx-react-lite';
import { TableRowFeatures } from '../../../component/table/table.component';
import GlobalNotification from '../../../component/common/snackbar';
import moment from 'moment';
import { Box, Button, Collapse, Grid, IconButton, InputAdornment, Stack, Typography } from '@mui/material';
import StatusCheckLabel from '../../../component/status-check-label';
import copy from 'copy-to-clipboard';
import { CoreFileLog, FiskalyClientInfo, GybDevice, SecretKey, Staff } from '../../../models';
import AsyncCheckboxWithLoading from '../../../component/async-checkbox-with-loading';
import { useTranslation } from 'react-i18next';
import { accountState } from '../../my-account/account-state';
import { LoadGoogleMap } from '../../my-restaurant/map/google-maps-provider';
import { COUNTRY_REG_CITY_REGULAR, COUNTRY_REG_NAME_REGULAR, COUNTRY_REG_POSTCODE_REGULAR, COUNTRY_VAT_REGULAR } from '../../../constants/regular.constant';
import { SPAIN_CITY_AREA } from '../../../constants/spain-city-area';
import CommonInput from '../../../component/form/common-input.component';
import { getQopEfrTunnelHost } from '../../../constants/tool.fn';
import {
    shouldShowFiskalyActivityCodeColumn,
    shouldShowFiskalySignInfoList,
} from './fiskaly-sign-info-table';
import { isFiscalCodeValidForCopy } from '../tax-code-copy';
import {
    ArchiveFormWorkbench,
    ArchiveOcrBanner,
    ArchiveOcrDocumentPane,
    calculateArchiveFormProgress,
    collectRestaurantSectionOcrKeys,
    countOcrFieldsForSectionKeys,
    countOcrReview,
    type AnagraficaOcrParseResult,
    type ArchiveFormSectionRule,
} from '../../../component/archive-form';
import {
    getRestaurantArchiveSections,
    getRestaurantCoreCardConfig,
    RESTAURANT_ARCHIVE_SECTION_HINTS,
} from './restaurant-archive-config';
import { isBelongedResellerInputDisabled } from './restaurant-reseller';
import { RestaurantDualAddress } from './restaurant-dual-address.component';
import { SkeletonOfRestaurantInfo } from './restaurant-info-skeleton';
import { ReactComponent as ChevronDownIcon } from '../../../static/images/svg/tabler_chevron-down.svg';
import './scss/restaurant-archive.style.scss';

/** 旧版配置仅保留四个核心分区之外的扩展模块。 */
const LegacyRestaurantInfoCardConfig: CardFormConfig[] = [
    // 主要信息数据
    {
        key: 'main',
        title: 'restaurant_info_page_text_main_information',
        rowSpacing: 6,
        columnSpacing: { md: 4, xs: 4 },
        children: [
            {
                comType: 'AVATAR',
                key: 'avatar',
                xs: 12
            },
            {
                key: 'country_code',
                label: 'restaurant_Country_and_Region',
                required: true,
                inputType: 'FLAG_INPUT',
                comType: 'INPUT',
                xs: 4
            },
            {
                key: 'nickname',
                label: 'restaurant_info_page_text_restaurant_name',
                required: true,
                regularType: 'inputLimit120',
                comType: 'INPUT',
                inputType: 'INPUT',
                xs: 8
            },
            {
                key: 'ex_string_id',
                label: 'restaurant_page_ex_string_id',
                comType: 'INPUT',
                inputType: 'INPUT',
                xs: 6
            },
            {
                key: 'setting_restaurant_operating_status',
                label: 'restaurant_info_page_status',
                inputType: 'SELECT',
                comType: 'INPUT',
                xs: 6,
                items: [
                    {
                        value: '1',
                        label: 'restaurant_info_page_restaurant_open'
                    },
                    {
                        value: '2',
                        label: 'restaurant_info_page_restaurant_close'
                    },
                    {
                        value: '3',
                        label: 'restaurant_page_text_restaurant_change_ditta'
                    },
                    {
                        value: '4',
                        label: 'restaurant_info_page_restaurant_transfer'
                    },
                    {
                        value: '5',
                        label: 'restaurant_info_page_restaurant_pause'
                    },
                    {
                        value: '6',
                        label: 'restaurant_info_page_restaurant_changed_system'
                    },
                    {
                        value: '7',
                        label: 'restaurant_info_page_restaurant_sosreso'
                    },
                    {
                        value: '8',
                        label: 'restaurant_info_page_restaurant_annullato'
                    }
                ],
            },
            {
                key: 'description',
                label: 'restaurant_info_page_text_description',
                inputType: 'INPUT',
                comType: 'INPUT',
                multiline: true,
                multiRowsNumber: 3,
                xs: 12
            },
            {
                key: 'belonged_group',
                label: 'general_text_belonged_group',
                required: true,
                inputType: 'SELECT',
                comType: 'INPUT',
                xs: 12
            },
            {
                key: 'time_zone',
                label: 'restaurant_info_page_text_time_zone',
                inputType: 'SELECT',
                comType: 'INPUT',
                xs: 12
            }, {
                key: 'restaurant_version',
                label: 'restaurant_page_input_text_restaurant_version_source',
                inputType: 'SELECT',
                comType: 'INPUT',
                xs: 12
            },
        ]
    },
    // 联系方式&客户销售信息
    {
        key: 'contact&customer_sale_information',
        title: '',
        rowSpacing: 6,
        columnSpacing: { md: 4, xs: 4 },
        subCardConfig: [
            // 联系方式
            {
                key: 'contact',
                title: 'restaurant_info_page_text_contact',
                rowSpacing: 6,
                columnSpacing: { md: 4, xs: 4 },
                children: [{
                    key: 'restaurant_contact_person',
                    label: 'restaurant_info_page_text_contact_person',
                    inputType: 'INPUT',
                    comType: 'INPUT',
                    xs: 12,
                    md: 12
                },
                {
                    key: 'email',
                    label: 'general_text_email',
                    inputType: 'INPUT',
                    comType: 'INPUT',
                    regularType: 'email',
                    xs: 12,
                    md: 12
                },
                {
                    key: 'tel_prefix',
                    label: 'general_text_country_code',
                    inputType: 'COUNTRY_SELECT',
                    comType: 'INPUT',
                    xs: 2,
                    md: 2
                },
                {
                    key: 'tel',
                    label: 'restaurant_info_page_text_phone',
                    inputType: 'INPUT',
                    comType: 'INPUT',
                    required: true,
                    regularType: 'phone',
                    xs: 10,
                    md: 10
                },
                {
                    key: 'website',
                    label: 'restaurant_info_page_text_website',
                    inputType: 'INPUT',
                    comType: 'INPUT',
                    xs: 12,
                    md: 12
                },

                ]
            },
            // 客户销售信息
            {
                key: 'customer_sale_information',
                title: 'restaurant_info_page_text_customer_sales_information',
                rowSpacing: 6,
                columnSpacing: { md: 4, xs: 4 },
                children: [
                    {
                        key: 'belonged_reseller',
                        label: 'reseller_page_text_reseller',
                        inputType: 'SELECT' as const,
                        comType: 'INPUT',
                        xs: 12,
                        md: 6
                    },
                    {
                        key: 'business_labels',
                        label: 'restaurant_info_page_text_service_instance_tag',
                        inputType: 'SELECT_MUL' as const,
                        comType: 'INPUT',
                        xs: 12,
                        md: 6
                    },
                    {
                        key: 'authorization_type',
                        label: 'restaurant_info_page_text_way_of_authorization',
                        inputType: 'SELECT',
                        comType: 'INPUT',
                        xs: 12,
                        md: 6
                    },
                    {
                        key: 'discount_info',
                        label: 'restaurant_info_page_text_discount_information_discount_module',
                        comType: 'INPUT',
                        contentType: 'number',
                        InputProps: {
                            endAdornment: <InputAdornment position='end'>%</InputAdornment>
                        },
                        xs: 12,
                        md: 6
                    },
                    {
                        key: 'discount_annual',
                        label: 'restaurant_info_page_text_discount_information_discount_annual',
                        comType: 'INPUT',
                        contentType: 'number',
                        InputProps: {
                            endAdornment: <InputAdornment position='end'>%</InputAdornment>
                        },
                        xs: 12,
                        md: 6
                    },
                    {
                        key: 'customer_mark',
                        label: 'restaurant_info_page_text_customer_marks',
                        inputType: 'INPUT',
                        comType: 'INPUT',
                        multiline: true,
                        multiRowsNumber: 3,
                        xs: 12
                    }
                ]
            },]
    },
    // 地址信息数据
    {
        key: 'address',
        title: 'restaurant_info_page_text_address',
        rowSpacing: 6,
        columnSpacing: { md: 4, xs: 4 },
        children: [
            {
                key: 'local',
                label: 'address_component_text_address',
                inputType: 'GOOGLE_PLACE',
                comType: 'INPUT',
                xs: 12,
                md: 12,
            },
            {
                key: 'home_umber',
                label: 'restaurant_info_page_text_home_number',
                inputType: 'INPUT',
                comType: 'INPUT',
                xs: 6,
                md: 4
            },
            {
                key: 'postcode',
                label: 'restaurant_info_page_text_postcode',
                inputType: 'INPUT',
                comType: 'INPUT',
                xs: 6,
                md: 4
            },
            {
                key: 'city',
                label: 'restaurant_info_page_text_city',
                inputType: 'INPUT',
                comType: 'INPUT',
                xs: 6,
                md: 4
            },
            {
                key: 'state',
                label: 'restaurant_info_page_text_state',
                inputType: 'INPUT',
                comType: 'INPUT',
                xs: 6,
                md: 6
            },
            {
                key: 'country',
                label: 'restaurant_info_page_text_country',
                inputType: 'INPUT',
                comType: 'INPUT',
                xs: 6,
                md: 6
            },
        ]
    },
    // 会计师信息
    {
        key: 'accountant',
        title: 'restaurant_info_page_text_accountant_information',
        rowSpacing: 6,
        columnSpacing: { md: 4, xs: 4 },
        children: [{
            key: 'accountant_name',
            label: 'restaurant_info_page_text_contact_person',
            inputType: 'INPUT',
            comType: 'INPUT',
            xs: 12,
            md: 12
        },
        {
            key: 'accountant_phone_prefix',
            label: 'general_text_country_code',
            inputType: 'COUNTRY_SELECT',
            comType: 'INPUT',
            xs: 2,
            md: 2
        },
        {
            key: 'accountant_phone',
            label: 'restaurant_info_page_text_phone',
            inputType: 'INPUT',
            comType: 'INPUT',
            regularType: 'phone',
            xs: 10,
            md: 10
        }
        ]
    },
    // 员工信息
    {
        key: 'staff_info',
        title: 'restaurant_info_page_text_owner_information',
        md: 12,
        xs: 12,
        hidden: true,
        children: [
            {
                key: 'staff_info_list',
                comType: 'TABLE',
                xs: 12,
            },
        ]
    },
    // 餐厅税务信息
    {
        key: 'tax',
        title: 'restaurant_info_page_text_restaurant_tax_information',
        rowSpacing: 6,
        children: [
            {
                key: 'reg_name',
                label: 'restaurant_info_page_text_company',
                inputType: 'INPUT',
                comType: 'INPUT',
                required: true,
                regularType: 'regName',
                xs: 12
            },
            {
                key: 'reg_fiscal_code',
                label: 'restaurant_info_page_text_fiscal_code',
                inputType: 'INPUT',
                comType: 'INPUT',
                required: true,
                regularType: 'fiscalCode',
                xs: 12
            },
            {
                key: 'reg_vat_number',
                label: 'restaurant_info_page_text_vat_number',
                inputType: 'INPUT',
                comType: 'INPUT',
                required: true,
                regularType: 'vatNumber',
                xs: 12
            },
            {
                key: 'reg_pec',
                label: 'restaurant_info_page_text_PEC',
                inputType: 'INPUT',
                comType: 'INPUT',
                required: true,
                regularType: 'email',
                xs: 12,
                md: 0
            },
            {
                key: 'reg_des_code',
                label: 'restaurant_info_page_text_client_recipient_code',
                inputType: 'INPUT',
                comType: 'INPUT',
                regularType: 'recipientCode',
                xs: 12
            },
        ]
    },
    // 餐厅税务地址
    {
        key: 'taxAddress',
        title: 'restaurant_info_page_text_restaurant_tax_address',
        rowSpacing: 6,
        columnSpacing: { md: 4, xs: 4 },
        children: [
            {
                key: 'reg_local',
                label: 'address_component_text_address',
                inputType: 'INPUT',
                comType: 'INPUT',
                xs: 12,
                md: 12
            },
            {
                key: 'reg_additional_address',
                label: 'restaurant_info_page_text_additional_address',
                inputType: 'INPUT',
                comType: 'INPUT',
                xs: 12,
                md: 12
            },
            {
                key: 'reg_postcode',
                label: 'restaurant_info_page_text_postcode',
                inputType: 'INPUT',
                comType: 'INPUT',
                xs: 6,
                md: 6,
            },
            {
                key: 'reg_city',
                label: 'restaurant_info_page_text_city',
                inputType: 'INPUT',
                comType: 'INPUT',
                xs: 6,
                md: 6
            },
            {
                key: 'reg_state',
                label: 'restaurant_info_page_text_state',
                inputType: 'INPUT',
                comType: 'INPUT',
                regularType: 'regName',
                xs: 6,
                md: 6
            },
            {
                key: 'reg_country',
                label: 'restaurant_info_page_text_country',
                inputType: 'INPUT',
                comType: 'INPUT',
                regularType: 'regName',
                xs: 6,
                md: 6
            },
            //市区
            {
                key: 'reg_municipality',
                label: 'restaurant_info_page_text_district',
                inputType: 'INPUT',
                comType: 'INPUT',
                regularType: 'regNameSpain120',
                md: 6,
                xs: 12,
                inputProps: {
                    minLength: 1,
                    maxLength: 120
                }
            },
            // 门牌号
            {
                key: 'reg_street_number',
                label: 'restaurant_info_page_text_number',
                inputType: 'INPUT',
                comType: 'INPUT',
                regularType: 'regNameSpain20',
                md: 6,
                xs: 12,
                inputProps: {
                    minLength: 1,
                    maxLength: 20
                }
            },
            // 区域
            {
                key: 'reg_territory',
                label: 'restaurant_info_page_text_region',
                inputType: 'SELECT',
                comType: 'INPUT',
                md: 6,
                xs: 12,
                items: SPAIN_CITY_AREA
            },
        ]
    },
    // 餐厅法人信息
    {
        key: 'legal_person',
        title: 'warrant_page_text_legal_entity_info',
        rowSpacing: 6,
        columnSpacing: { md: 4, xs: 4 },
        children: [
            {
                key: 'corp_name',
                label: 'warrant_page_text_legal_entity_name',
                inputType: 'INPUT',
                comType: 'INPUT',
                md: 6,
                xs: 12,
            },
            {
                key: 'corp_vat_number',
                label: 'restaurant_info_page_text_vat_number',
                inputType: 'INPUT',
                comType: 'INPUT',
                md: 6,
                xs: 12,
            },
            {
                key: 'corp_local',
                label: 'address_component_text_address',
                inputType: 'INPUT',
                comType: 'INPUT',
                xs: 12,
                md: 6
            },
            {
                key: 'corp_additional_address',
                label: 'restaurant_info_page_text_additional_address',
                inputType: 'INPUT',
                comType: 'INPUT',
                xs: 12,
                md: 6
            },
            {
                key: 'corp_postcode',
                label: 'restaurant_info_page_text_postcode',
                inputType: 'INPUT',
                comType: 'INPUT',
                md: 3,
                xs: 12,
            },
            {
                key: 'corp_city',
                label: 'restaurant_info_page_text_city',
                inputType: 'INPUT',
                comType: 'INPUT',
                md: 3,
                xs: 12,
            },
            {
                key: 'corp_state',
                label: 'restaurant_info_page_text_state',
                inputType: 'INPUT',
                comType: 'INPUT',
                regularType: 'regName',
                md: 3,
                xs: 12,
            },
            {
                key: 'corp_country',
                label: 'restaurant_info_page_text_country',
                inputType: 'INPUT',
                comType: 'INPUT',
                regularType: 'regName',
                md: 3,
                xs: 12,
            },
            //市区
            {
                key: 'corp_municipality',
                label: 'restaurant_info_page_text_district',
                inputType: 'INPUT',
                comType: 'INPUT',
                regularType: 'regNameSpain120',
                md: 3,
                xs: 12,
                inputProps: {
                    minLength: 1,
                    maxLength: 120
                }
            },
            // 门牌号
            {
                key: 'corp_street_number',
                label: 'restaurant_info_page_text_number',
                inputType: 'INPUT',
                comType: 'INPUT',
                regularType: 'regNameSpain20',
                md: 3,
                xs: 12,
                inputProps: {
                    minLength: 1,
                    maxLength: 20
                }
            },
        ]
    },
    // 餐厅扩展功能信息
    {
        key: 'extensions',
        title: 'restaurant_extensions_page_text_restaurant_extension_function_information',
        md: 12,
        xs: 12,
        hidden: true,
        children: [
            {
                key: 'extensions_list',
                comType: 'TABLE',
                xs: 12,
            },
        ]
    },

    // GYB管理信息
    {
        key: 'gyb_info',
        title: 'gyb_page_text_GYB_management_information',
        md: 12,
        xs: 12,
        hidden: true,
        children: [
            {
                key: 'gyb_info_list',
                comType: 'TABLE',
                xs: 12,
            },
        ]
    },
    // Aruba认证信息
    {
        key: 'aruba',
        title: 'gyb_page_text_aruba_authentication_information',
        md: 6,
        rowSpacing: 6,
        columnSpacing: { md: 4, xs: 4 },
        hidden: true,
        children: [
            {
                key: 'Aruba_username',
                label: 'restaurant_page_input_text_aruba_account',
                inputType: 'INPUT',
                comType: 'INPUT',
                xs: 12,
                md: 4
            },
            {
                key: 'Aruba_password',
                label: 'restaurant_page_input_text_aruba_password',
                inputType: 'INPUT',
                comType: 'INPUT',
                xs: 12,
                md: 4
            },
            {
                key: 'activation_time',
                label: 'restaurant_page_input_text_active_date',
                inputType: 'DATE',
                comType: 'INPUT',
                xs: 12,
                md: 4,
            },
        ]
    },
    // Plateform Web API（第三方预订，紧挨 Aruba 认证信息下方、一行两个）
    {
        key: 'plateform_api',
        title: 'restaurant_page_title_plateform_web_api',
        md: 6,
        rowSpacing: 6,
        columnSpacing: { md: 4, xs: 4 },
        children: [
            {
                key: 'plateform_api_user',
                label: 'restaurant_page_input_text_plateform_api_user',
                inputType: 'INPUT',
                comType: 'INPUT',
                xs: 12,
                md: 6
            },
            {
                key: 'plateform_api_password',
                label: 'restaurant_page_input_text_plateform_api_password',
                inputType: 'INPUT',
                comType: 'INPUT',
                xs: 12,
                md: 6
            },
        ]
    },
    // 通道
    {
        key: 'tunnel',
        title: 'restaurant_page_input_text_restaurant_tunnel_info_configuration',
        md: 6,
        rowSpacing: 6,
        columnSpacing: { md: 4, xs: 4 },
        children: [
            {
                key: 'database_host',
                label: 'restaurant_page_input_text_restaurant_host',
                inputType: 'INPUT',
                comType: 'INPUT',
                regularType: 'description',
                xs: 12,
                md: 6
            },
            {
                key: 'database_port',
                label: 'restaurant_page_input_text_restaurant_port',
                inputType: 'INPUT',
                comType: 'INPUT',
                regularType: 'maxNumberSix',
                xs: 12,
                md: 6
            },
            {
                key: 'database_port_array',
                comType: 'CUSTOMCHILDREN',
                xs: 12,
                md: 12
            },
            {
                key: 'head',
                label: 'restaurant_info_page_text_qop_WAN_port_address',
                inputType: 'SELECT',
                comType: 'INPUT',
                xs: 12,
                md: 2
            },
            {
                key: 'url',
                inputType: 'INPUT',
                comType: 'INPUT',
                xs: 12,
                md: 4,
                label: 'ECCO'
            },

            {
                key: 'port',
                label: 'restaurant_info_page_text_local_port',
                inputType: 'INPUT',
                comType: 'INPUT',
                regularType: 'maxNumberSix',
                xs: 12,
                md: 2
            },
            {
                key: 'qop_efr_url_display',
                comType: 'CUSTOMCHILDREN',
                xs: 12,
                md: 4
            },
        ]
    },
    // core file相关  餐厅版本信息2.5
    {
        key: 'core_file',
        title: 'core_file_component_core_file_related',
        md: 12,
        xs: 12,
        hidden: true,
        children: [
            {
                key: 'core_file_list',
                comType: 'TABLE',
                xs: 12,
            },
        ]
    },
    // Fiskaly 设置
    {
        key: 'fiskaly_settings',
        title: 'Fiskaly',
        rowSpacing: 6,
        columnSpacing: { md: 4, xs: 4 },
        md: 12,
        xs: 12,
        children: [
            {
                key: 'fiskaly_manage_organization_key',
                label: 'Fiskaly API KEY',
                inputType: 'INPUT',
                comType: 'INPUT',
                xs: 6,
                disabledInput: true
            },
            {
                key: 'fiskaly_manage_organization_secret',
                label: 'Fiskaly API SECRET',
                inputType: 'INPUT',
                comType: 'INPUT',
                xs: 6,
                disabledInput: true
            },
            {
                key: 'fiskaly_manage_organization_user_id',
                comType: 'CUSTOMCHILDREN',
                xs: 12,
            },
            {
                key: 'fiskaly_sign_info',
                comType: 'TABLE',
                xs: 12,
            },
            {
                key: 'fiskaly_client_info',
                comType: 'TABLE',
                xs: 12,
            },
        ]
    }
]

/** 四个核心分区之后允许继续展示的既有扩展模块。 */
const RESTAURANT_EXTENSION_SECTION_KEYS = [
    'legal_person',
    'extensions',
    'gyb_info',
    'aruba',
    'plateform_api',
    'tunnel',
    'core_file',
    'fiskaly_settings',
    'staff_info',
] as const;

/**
 * 按既定顺序提取餐厅扩展模块，避免核心分区重排改变扩展业务配置。
 * @param config 包含扩展模块的原配置
 * @returns 顺序稳定的扩展模块
 */
const getRestaurantExtensionSections = (
    config: CardFormConfig[],
): CardFormConfig[] => RESTAURANT_EXTENSION_SECTION_KEYS
    .map((key) => config.find((section) => section.key === key))
    .filter((section): section is CardFormConfig => Boolean(section));

/**
 * 将法人信息插入税务与开票分区之后，保持其余分区顺序不变。
 * @param coreSections 餐厅建档核心分区
 * @param extensionSections 餐厅扩展分区
 * @returns 按页面展示顺序合并后的分区配置
 */
const getRestaurantCardSections = (
    coreSections: CardFormConfig[],
    extensionSections: CardFormConfig[],
): CardFormConfig[] => {
    /** 法人信息分区，需要紧跟税务与开票分区展示。 */
    const legalPersonSection = extensionSections.find((section) => section.key === 'legal_person');
    /** 除法人信息外的扩展分区，保持既有顺序。 */
    const remainingExtensionSections = extensionSections.filter((section) => section.key !== 'legal_person');
    /** 税务与开票分区在核心配置中的位置。 */
    const taxAndAddressIndex = coreSections.findIndex((section) => section.key === 'tax-and-address');

    if (!legalPersonSection || taxAndAddressIndex < 0) {
        return [...coreSections, ...extensionSections];
    }

    return [
        ...coreSections.slice(0, taxAndAddressIndex + 1),
        legalPersonSection,
        ...coreSections.slice(taxAndAddressIndex + 1),
        ...remainingExtensionSections,
    ];
};

/** 餐厅页面默认配置；运行时会按当前国家和页面模式补充动态规则。 */
export const RestaurantInfoCardConfig: CardFormConfig[] = [
    ...getRestaurantCardSections(
        getRestaurantCoreCardConfig({
            componentType: 'ADD',
            countryCode: '+39',
            isReseller: false,
            restaurantVersion: '2.0',
        }),
        getRestaurantExtensionSections(LegacyRestaurantInfoCardConfig),
    ),
];

/** 低频餐厅字段折叠容器属性。 */
export interface RestaurantCollapsibleFieldProps {
    /** 字段标签 i18n key。 */
    label: string;
    /** 当前字段值；有值时默认展开。 */
    value?: string;
    /** 展开后展示的 CommonInput。 */
    children: React.ReactNode;
}

/**
 * 展示默认收起、有值自动展开的低频餐厅字段。
 * @param props 字段标签、当前值与输入控件
 * @returns 可展开的字段容器
 */
export function RestaurantCollapsibleField({
    label,
    value = '',
    children,
}: RestaurantCollapsibleFieldProps) {
    /** 有既有内容时默认展开，避免编辑态隐藏已保存数据。 */
    const [expanded, setExpanded] = React.useState(Boolean(value));
    return (
        <Stack className="restaurant-collapsible-field" spacing={1}>
            <Stack direction="row" alignItems="center" className="restaurant-collapsible-field__header">
                <IconButton
                    aria-label={i18n.t(expanded ? 'archive_form_collapse' : 'archive_form_expand')}
                    aria-expanded={expanded}
                    onClick={() => setExpanded((current) => !current)}
                >
                    <ChevronDownIcon className={expanded ? 'restaurant-collapsible-field__icon--expanded' : ''} />
                </IconButton>
                <Typography variant="body2">{i18n.t(label)}</Typography>
            </Stack>
            <Collapse in={expanded} unmountOnExit>
                <Stack className="restaurant-collapsible-field__content" spacing={1}>
                    {children}
                </Stack>
            </Collapse>
        </Stack>
    );
}

/** 表单 init 完成后取出的待填 OCR 结果。 */
type PendingOcrConsume = () => { result: AnagraficaOcrParseResult; file: File } | null;

export default observer(function RestaurantInfo(props: Readonly<{
    restaurantInfoState: RestaurantInfoState,
    RestaurantInfoCardConfig: CardFormConfig[],
    /** 可选；扫描建档时在 init 之后消费，避免与表单重置竞态。 */
    consumePendingOcr?: PendingOcrConsume,
}>) {
    const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
    useTranslation();
    // const useData = MobxObjectsManager.get(RestaurantInfoState);
    // useManualRemove(RestaurantInfoState);
    const useData = props.restaurantInfoState;

    // 骨架屏
    const [skeleton, enableSkeleton] = React.useState<boolean>(true);
    /** Google Maps 脚本是否已完成加载，用于通知地址输入组件初始化服务。 */
    const [googleMapsLoaded, setGoogleMapsLoaded] = React.useState<boolean>(false);

    React.useEffect(() => {
        initDialog();
        return () => {
            // 卸载清空表单数据
            useData.clearFormInfo();
        }
    }, []);

    /**
     * 先完成表单 init，再写入暂存的 OCR，保证预览 URL 不被 initEditRestaurantData 释放。
     */
    const initDialog = async () => {
        enableSkeleton(true);
        await useData.init();
        const pendingOcr = props.consumePendingOcr?.() ?? null;
        if (pendingOcr) {
            useData.queueOcrParse(pendingOcr.result, pendingOcr.file);
        }
        enableSkeleton(false);
    }

    // 员工绑定
    const staffInfoListRenderFeatures = (row: Staff): React.ReactNode => {
        return (
            <TableRowFeatures
                buttons={[
                    {
                        variant: 'contained',
                        color: 'secondary',
                        titleText: i18n.t('general_text_delete'),
                        children: (<CloseSharp />),
                        handleClick: () => {
                            return useData.updateConfirmDialog({
                                visible: true,
                                title: '',
                                content: '',
                                handleClick: () => { return row._id && useData.unbind(row); }
                            })
                        }
                    }
                ]} />
        )
    }

    // 密钥
    const extensionsRenderFeatures = (row: SecretKey): React.ReactNode => {
        return (
            <TableRowFeatures
                buttons={[
                    {
                        color: 'secondary',
                        action: 'DELETE',
                        handleClick: () => {
                            return useData.updateConfirmDialog({
                                visible: true,
                                title: '',
                                content: '',
                                handleClick: () => { return row._id && useData.deleteSecretKey(row._id); }
                            })
                        }
                    }, {
                        action: 'REFRESH',
                        handleClick: () => {
                            return useData.updateConfirmDialog({
                                visible: true,
                                title: '',
                                content: i18n.t('restaurant_extensions_page_text_do_you_need_refresh_key'),
                                handleClick: () => { return row._id && useData.refreshSecretKey(row._id); }
                            })
                        }
                    }, {
                        action: 'VIEW',
                        handleClick: () => {
                            return useData.updateExtensionsDialog({
                                open: true, secretKey: row, dialogType: 'EDIT',
                                onSave: async (params) => { await useData.editExtensions(params); }
                            })
                        }
                    }, {
                        handleClick: () => {
                            useData.downloadKeyFile(row);
                        },
                        titleText: i18n.t('general_text_download'),
                        children: <GetApp />
                    }
                ]} />
        )
    }

    // Fiskaly Client Info
    const fiskalyClientInfoRenderFeatures = (row: FiskalyClientInfo): React.ReactNode => {
        return (
            <TableRowFeatures
                buttons={[
                    {
                        color: 'secondary',
                        action: 'DELETE',
                        handleClick: () => {
                            return useData.updateConfirmDialog({
                                visible: true,
                                title: '',
                                content: '',
                                handleClick: async () => await useData.unbindDisableFiskalyClientInfo(row, 'REMOVED')
                            })
                        }
                    }
                ]} />
        )
    }
    // GYB
    const gybInfoRenderFeatures = (row: GybDevice): React.ReactNode => {

        return (
            <TableRowFeatures
                buttons={[
                    {
                        color: 'secondary',
                        titleText: i18n.t('general_text_delete'),
                        children: (<CloseSharp />),
                        handleClick: () => {
                            useData.updateConfirmDialog({
                                visible: true,
                                title: '',
                                content: '',
                                handleClick: () => { return useData.GYBhandleDelete(row); }
                            })
                        }
                    }, {
                        titleText: i18n.t('gyb_page_text_set_default_device'),
                        children: (<Settings />),
                        handleClick: () => {
                            useData.updateConfirmDialog({
                                visible: true,
                                title: '',
                                content: i18n.t('gyb_page_text_set_default_device'),
                                handleClick: () => { return useData.handleDefaultSetting(row); }
                            })
                        }
                    }, {
                        titleText: i18n.t('general_text_details'),
                        children: (<RateReview />),
                        handleClick: () => {
                            useData.updateGYBDialog({
                                open: true,
                                dialogType: 'PREVIEW',
                                onSaveCallback: useData.onSaveGYB,
                                GYBDeviceInfo: row
                            });
                        }
                    }, {
                        titleText: i18n.t('operation_record_refresh_function_key'),
                        children: (<Refresh />),
                        handleClick: () => {
                            useData.handleUpdate(row);
                        }
                    }
                ]} />
        )
    }

    const coreFileLogRenderFeatures = (row: CoreFileLog): React.ReactNode => {
        return (
            <TableRowFeatures
                buttons={[
                    {
                        titleText: i18n.t('general_text_details'),
                        children: (<RateReview />),
                        handleClick: () => {
                            return useData.updateCoreFlieLogDialog({
                                open: true, info: row, dialogType: 'PREVIEW',
                                onSave: (params) => { useData.editExtensions(params); }
                            })
                        }
                    },
                ]} />
        )
    }

    /**
     * 渲染双端口输入区，保持 ECCO/EFR 项目唯一。
     * @returns 双端口表单区域
     */
    const renderMultiDatabasePortInputs = (): React.ReactNode => {
        // 预览态与经销商账号不可编辑双端口配置
        const disabled = useData.componentType === 'PREVIEW' || accountState.accountType === 'RESELLER';
        return (
            <Grid container spacing={2}>
                {useData.multiDatabasePortItems.map((item, index) => (
                    <React.Fragment key={`${item.project}_${index}`}>
                        <Grid item xs={12} md={3}>
                            <CommonInput
                                inputType='SELECT'
                                fullWidth
                                label='Project'
                                size='small'
                                disabled={disabled}
                                items={useData.getDatabaseTunnelProjectOptions()}
                                value={item.project}
                                onChange={(event) => {
                                    useData.updateMultiDatabasePortProject(index, event.target.value as any);
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <CommonInput
                                inputType='INPUT'
                                fullWidth
                                label='restaurant_page_input_text_restaurant_port'
                                size='small'
                                disabled={disabled}
                                value={item.port}
                                inputProps={{ maxLength: 6 }}
                                onChange={(event) => {
                                    useData.updateMultiDatabasePortPort(index, event.target.value);
                                }}
                            />
                        </Grid>
                    </React.Fragment>
                ))}
            </Grid>
        );
    }

    /** 按当前页面状态重建核心分区，扩展模块继续沿用调用方既有配置。 */
    const coreCardConfig = getRestaurantCoreCardConfig({
        componentType: useData.componentType,
        countryCode: useData.editRestaurantData.country_code || '',
        isReseller: accountState.accountType === 'RESELLER',
        restaurantVersion: useData.editRestaurantData.restaurant_version || '',
    });
    /** 当前页面可见的扩展分区，法人信息将在税务与开票分区后插入。 */
    const extensionCardConfig = getRestaurantExtensionSections(props.RestaurantInfoCardConfig);
    const baseCardConfig = getRestaurantCardSections(coreCardConfig, extensionCardConfig);

    const cardConfig: CardFormConfig[] = baseCardConfig.map((item) => {
        let extraParentConfig = {};
        // 是否是意大利
        const isItaly = useData.editRestaurantData.country_code === '+39';
        // 是否是西班牙
        const isSpain = useData.editRestaurantData.country_code === '+34';
        // 法国 SIGN FR（与西班牙共用开店步骤区，完成态读 fiskaly_fr_*）
        const isFrance = useData.editRestaurantData.country_code === '+33';
        // 西班牙或法国均可显示 Fiskaly 开店步骤 1–3（法国步骤 4 在税务区）
        const showFiskalyOnboardingSteps = isSpain || isFrance;
        // 是否有fiskaly信息
        const { fiskaly_signer_id, fiskaly_serial_number, fiskaly_signer_status } =
            useData.restaurantInfo || {};
        // 是否有fiskaly信息
        const hasFiskalyInfo =
            !!(fiskaly_signer_id || fiskaly_serial_number);


        // 隐藏第一步骤，第一步骤一成功执行，有组织 id
        const isHiddenStepOne = isFrance
            ? !!useData.restaurantInfo.fiskaly_fr_unit_organization_id
            : useData.restaurantInfo.fiskaly_manage_organization_id;

        // 隐藏第二步骤，第二步骤成功执行，有管理 key 和 secret
        const isHiddenStepTwo = isFrance
            ? !!(useData.restaurantInfo.fiskaly_fr_unit_api_key && useData.restaurantInfo.fiskaly_fr_unit_api_secret)
            : (useData.editRestaurantData.fiskaly_manage_organization_key && useData.editRestaurantData.fiskaly_manage_organization_secret);

        // 隐藏第三步骤，第三步骤已成功执行
        const isHiddenStepThree = isFrance
            ? !!useData.restaurantInfo.fiskaly_fr_taxpayer_status
            : useData.restaurantInfo.taxpayer_status;

        // 隐藏第四步骤：法国为 Location+System；西班牙仍为 Verifactu 协议（legal_person 区）
        const isHiddenStepFour = isFrance
            ? !!(useData.restaurantInfo.fiskaly_fr_location_id && useData.restaurantInfo.fiskaly_fr_system_id)
            : (useData.isDisabledLegalPerson && useData.restaurantInfo.restaurant_unsigned_agreement);

        // 商家声明是否上传
        const isMerchantStatement = useData.restaurantInfo.fiskaly_upload_agreemen === '1' && useData.restaurantInfo.restaurant_agreement;

        // 显示禁用 Fiskaly Client Info 按钮（仅西班牙）
        const showDisableFiskalyClientInfoButton = !isFrance
            && useData.restaurantInfo.fiskaly_manage_organization_id
            && useData.restaurantInfo.fiskaly_signer_status !== 'DISABLED';

        // 预览协议
        const previewAgreementUrl = useData.isDisabledLegalPerson ? (useData.restaurantInfo.restaurant_agreement || useData.restaurantInfo.restaurant_unsigned_agreement || '') : useData.restaurantInfo.restaurant_unsigned_agreement;

        switch (item.key) {
            case 'aruba':
                extraParentConfig = {
                    hidden: !isItaly || useData.componentType && ['ADD', 'BASE_INFO'].includes(useData.componentType),
                }
                break;
            case 'tunnel':
                extraParentConfig = {
                    actionRender:
                        (<>
                            {
                                accountState.accountType === 'RESELLER' ? null :
                                    <ButtonComponent
                                        variant='outlined'
                                        disabled={useData.componentType === 'PREVIEW'}
                                        handleClick={async () => { await useData.handleGenerateTunnel('single') }}
                                        titleText={i18n.t('restaurant_page_tooltip_generate_single_tunnel')}
                                        iconBtn={false}
                                    >
                                        {i18n.t('restaurant_page_button_generate_single_tunnel')}
                                    </ButtonComponent>
                            }
                            {
                                accountState.accountType === 'RESELLER' ? null :
                                    <ButtonComponent
                                        variant='outlined'
                                        disabled={useData.componentType === 'PREVIEW'}
                                        color='info'
                                        handleClick={async () => { await useData.handleGenerateTunnel('multi') }}
                                        titleText={i18n.t('restaurant_page_tooltip_generate_dual_tunnel')}
                                        iconBtn={false}
                                    >
                                        {i18n.t('restaurant_page_button_generate_dual_tunnel')}
                                    </ButtonComponent>
                            }
                            <ButtonComponent
                                variant='outlined'
                                disabled={useData.componentType === 'ADD'}
                                handleClick={async () => { await useData.downloadFile() }}
                                titleText={i18n.t('general_text_download')}
                            >
                                <GetApp />
                            </ButtonComponent>
                        </>)
                }
                break;
            case 'fiskaly_settings':
                // 西班牙 Fiskaly 设置卡片（API KEY / 签名表等）；法国 SIGN FR 开店按钮在税务区，不展示本块
                extraParentConfig = {
                    hidden: !isSpain || !['EDIT'].includes(useData.componentType || ''),
                }
                break;
            case 'legal_person':
                extraParentConfig = {
                    hidden: !isSpain,
                    actionRender: ['EDIT'].includes(useData.componentType || '') ?
                        <Box display={'flex'} alignItems='center'>
                            {
                                !isHiddenStepFour ?
                                    <ButtonComponent
                                        variant='outlined'
                                        disabled={useData.componentType === 'ADD'}
                                        handleClick={async () => { await useData.getFiskalyCredentialsStepFour('4') }}
                                    >
                                        {`4. ${i18n.t('warrant_page_text_generate_agreement')}`}
                                    </ButtonComponent>
                                    :
                                    <div className="FCC">
                                        <StatusCheckLabel
                                            text={i18n.t('warrant_page_text_generated')}
                                        />
                                        {
                                            useData.isDisabledLegalPerson &&
                                            <ButtonComponent
                                                variant='outlined'
                                                action='VIEW'
                                                handleClick={() => useData.updateConfirmDialogLegalPerson()}
                                                titleText={i18n.t('general_text_edit')}
                                            />
                                        }
                                    </div>

                            }
                            {
                                useData.isDisabledLegalPerson &&
                                <>

                                    {
                                        isMerchantStatement ?
                                            <StatusCheckLabel
                                                text={i18n.t('fiskaly_setting_status_uploaded')}
                                            /> :
                                            <StatusCheckLabel
                                                status='fail'
                                                text={i18n.t('fiskaly_setting_status_not_uploaded')}
                                            />
                                    }
                                </>
                            }

                            <ButtonComponent
                                variant='outlined'
                                action='DETAIL'
                                handleClick={() => {
                                    useData.updatePreviewAgreementDialog({
                                        open: true,
                                        url: previewAgreementUrl || ''
                                    })
                                }}
                                titleText={i18n.t('general_text_preview')}
                            />

                        </Box>
                        :
                        null

                }
                break;
            case 'taxAddress':
                extraParentConfig = {
                    actionRender: (['EDIT'].includes(useData.componentType || '') && showFiskalyOnboardingSteps ?
                        <Box display={'flex'} alignItems='center' sx={{ height: '52px' }}>
                            {!isHiddenStepTwo ?
                                <ButtonComponent
                                    variant='outlined'
                                    disabled={useData.componentType === 'ADD'}
                                    handleClick={async () => { await useData.getFiskalyCredentialsStepOneTwoThree('2') }}
                                >
                                    {`2. ${i18n.t('warrant_page_text_generate_voucher')}`}
                                </ButtonComponent>
                                :
                                <StatusCheckLabel
                                    text={i18n.t('fiskaly_page_text_step_two_certificate_generated')}
                                />


                            }
                            {
                                !isHiddenStepThree ?
                                    <ButtonComponent
                                        variant='outlined'
                                        disabled={useData.componentType === 'ADD'}
                                        handleClick={async () => { await useData.getFiskalyCredentialsStepOneTwoThree('3') }}
                                    >
                                        {`3. ${i18n.t('warrant_page_text_register_taxpayer')}`}
                                    </ButtonComponent>
                                    :
                                    <StatusCheckLabel
                                        text={i18n.t('fiskaly_page_text_step_three_taxpayer_registered')}
                                    />
                            }
                        </Box> : null)
                }
                break;
            case 'tax-and-address':
            case 'tax':
                extraParentConfig = {
                    actionRender: (['EDIT'].includes(useData.componentType || '') && showFiskalyOnboardingSteps ?
                        <Box display={'flex'} alignItems='center' sx={{ height: '52px' }}>
                            {
                                !isHiddenStepOne ?
                                    <>
                                        <AsyncCheckboxWithLoading
                                            checked={!useData.editRestaurantData.fiskaly_environment}
                                            label={(
                                                <Typography variant='body2'>
                                                    Test
                                                </Typography>
                                            )}
                                            onChange={(val) => useData.updateEditRestaurantData(!val, 'fiskaly_environment')}
                                        />
                                        <ButtonComponent
                                            variant='outlined'
                                            disabled={useData.componentType === 'ADD'}
                                            handleClick={async () => { await useData.getFiskalyCredentialsStepOneTwoThree('1') }}
                                        >
                                            {`1. ${i18n.t('warrant_page_text_register_organization')}`}
                                        </ButtonComponent>
                                    </>
                                    :
                                    <StatusCheckLabel
                                        text={i18n.t('fiskaly_page_text_step_one_organization_registered')}
                                    />

                            }
                            {
                                !isHiddenStepTwo ?
                                    <ButtonComponent
                                        variant='outlined'
                                        disabled={useData.componentType === 'ADD'}
                                        handleClick={async () => { await useData.getFiskalyCredentialsStepOneTwoThree('2') }}
                                    >
                                        {`2. ${i18n.t('warrant_page_text_generate_voucher')}`}
                                    </ButtonComponent>
                                    :
                                    <StatusCheckLabel
                                        text={i18n.t('fiskaly_page_text_step_two_certificate_generated')}
                                    />
                            }
                            {
                                !isHiddenStepThree ?
                                    <ButtonComponent
                                        variant='outlined'
                                        disabled={useData.componentType === 'ADD'}
                                        handleClick={async () => { await useData.getFiskalyCredentialsStepOneTwoThree('3') }}
                                    >
                                        {`3. ${i18n.t('warrant_page_text_register_taxpayer')}`}
                                    </ButtonComponent>
                                    :
                                    <StatusCheckLabel
                                        text={i18n.t('fiskaly_page_text_step_three_taxpayer_registered')}
                                    />
                            }
                            {
                                isFrance && (
                                    !isHiddenStepFour ?
                                        <ButtonComponent
                                            variant='outlined'
                                            disabled={useData.componentType === 'ADD'}
                                            handleClick={async () => { await useData.getFiskalyCredentialsStepFour('4') }}
                                        >
                                            {`4. ${i18n.t('warrant_page_text_register_fr_location_system')}`}
                                        </ButtonComponent>
                                        :
                                        <StatusCheckLabel
                                            text={i18n.t('warrant_page_text_register_fr_location_system')}
                                        />
                                )
                            }
                            {
                                showDisableFiskalyClientInfoButton &&
                                <ButtonComponent
                                    variant='outlined'
                                    handleClick={() => useData.updateConfirmDialog({
                                        visible: true,
                                        title: '',
                                        content: i18n.t('restaurant_info_page_text_fiskaly_disable_client_and_signer_confirm'),
                                        handleClick: async () => await useData.unbindDisableTax()
                                    })}
                                    titleText={i18n.t('restaurant_info_page_text_fiskaly_disable_client_and_signer_title')}
                                >
                                    <NotInterestedIcon />
                                </ButtonComponent>
                            }
                        </Box> : null)
                }
                break;
            case 'staff_info':
                extraParentConfig = {
                    hidden: useData.componentType && ['ADD', 'BASE_INFO'].includes(useData.componentType),
                    actionRender: (useData.componentType === 'PREVIEW' ? <></> : <ButtonComponent
                        variant='outlined'
                        handleClick={() => {
                            useData.updateResponsabileDialog({ visible: true })
                        }}
                        titleText={i18n.t('bind_account_component_bind_new_account')}
                    >
                        <Add />
                    </ButtonComponent>)
                }
                break;
            case 'extensions':
                extraParentConfig = {
                    hidden: useData.componentType && ['ADD', 'BASE_INFO'].includes(useData.componentType),
                    actionRender: (
                        useData.componentType === 'PREVIEW' ? <></>
                            :
                            <TableRowFeatures
                                buttons={[
                                    {
                                        variant: 'outlined',
                                        titleText: i18n.t('general_text_add'),
                                        children: (<Add />),
                                        handleClick: () => {
                                            if (useData?.restaurantInfo?.secret_key?.length) {
                                                GlobalNotification.info(i18n.t('restaurant_extensions_page_text_only_five_keys_most'))
                                            } else {
                                                useData.updateExtensionsDialog({
                                                    open: true, dialogType: 'ADD',
                                                    onSave: async (params) => { await useData.createSecretKey(params); }
                                                })
                                            }
                                        }
                                    }, {
                                        variant: 'outlined',
                                        titleText: i18n.t('restaurant_info_page_text_private_key_fix_desc'),
                                        children: (<Settings />),
                                        handleClick: () => { useData.handleFixPrivateKey() }
                                    }
                                ]} />
                    )
                }
                break;
            case 'gyb_info':
                extraParentConfig = {
                    // 国家选择非意大利的时候， 电子发票收件人编码不显示； GYB管理信息和Aruba认证信息不显示
                    hidden: !isItaly || useData.componentType && ['ADD', 'BASE_INFO'].includes(useData.componentType),
                    // 经销商不能编辑gyb
                    actionRender: (useData.componentType === 'PREVIEW' || accountState.accountType === 'RESELLER' ? <></> : <ButtonComponent
                        variant='outlined'
                        handleClick={() => { useData.updateGYBDialog({ open: true, dialogType: 'ADD', onSaveCallback: useData.onSaveGYB }) }}
                        titleText={i18n.t('general_text_add')}
                    >
                        <Add />
                    </ButtonComponent>)
                }
                break;
            case 'core_file':
                extraParentConfig = {
                    hidden: (useData.componentType && ['ADD', 'BASE_INFO'].includes(useData.componentType)) || useData.editRestaurantData?.restaurant_version !== '2.5',
                    actionRender: (<ButtonComponent
                        variant='outlined'
                        handleClick={() => { useData.getCoreFileAasKey(); useData.getCoreFileRsaPrivateKey(); }}
                        titleText={i18n.t('general_text_download')}
                    >
                        <GetApp />
                    </ButtonComponent>)
                }
                break;
            default:
                break;
        }

        // 是否禁用输入框  reg_name、reg_territory、reg_vat_number，如果注册了Fiskaly就不能修改了
        const disabledTaxInput = useData.componentType === 'PREVIEW'
            || Boolean(
                useData.restaurantInfo.fiskaly_manage_organization_id
                && useData.restaurantInfo.fiskaly_signer_status !== 'DISABLED',
            );
        return {
            ...item,
            ...extraParentConfig,
            children: item?.children?.map((childrenItem) => {
                let extraConfig = {};
                switch (childrenItem.key) {
                    case 'avatar':
                        extraConfig = {
                            avatarSize: 64,
                            // 64px 建档图位：空态与悬浮操作按钮按原型缩小
                            avatarCompact: true,
                            // 餐厅建档表单不提供 stringId，禁止使用全局餐厅码开启网络搜图
                            disableNetworkUpload: true,
                            isDisabledUpload: useData.componentType === 'PREVIEW',
                            handleChangeAvatar: ((_avatarUrl: string, file: File) => {
                                return useData.onChangeAvatar(file);
                            })
                        }
                        break;
                    case 'string_id':
                        extraConfig = {
                            disabledInput: true,
                            inputProps: useData.componentType === 'ADD'
                                ? { placeholder: i18n.t('archive_form_string_id_placeholder') }
                                : undefined,
                            InputProps: useData.componentType !== 'ADD'
                                ? {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Button
                                                variant="text"
                                                onClick={() => {
                                                    const copied = copy(useData.editRestaurantData.string_id || '');
                                                    if (copied) {
                                                        GlobalNotification.success(
                                                            i18n.t('restaurant_page_input_tip_message_copy_successfully'),
                                                        );
                                                    }
                                                }}
                                            >
                                                <ContentCopy />
                                            </Button>
                                        </InputAdornment>
                                    ),
                                }
                                : undefined,
                        }
                        break;
                    case 'description':
                        extraConfig = {
                            customSubChildren: (
                                <RestaurantCollapsibleField
                                    label="archive_form_restaurant_description_fold"
                                    value={useData.editRestaurantData.description || ''}
                                >
                                    <CommonInput
                                        inputType="INPUT"
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label="restaurant_info_page_text_description"
                                        size="small"
                                        disabled={useData.componentType === 'PREVIEW'}
                                        value={useData.editRestaurantData.description || ''}
                                        onChange={(event) => {
                                            useData.updateEditRestaurantData(event.target.value, 'description');
                                        }}
                                    />
                                </RestaurantCollapsibleField>
                            ),
                        }
                        break;
                    case 'dual_address':
                        extraConfig = {
                            customSubChildren: (
                                <RestaurantDualAddress
                                    restaurantInfoState={useData}
                                    googleMapsLoaded={googleMapsLoaded}
                                    // 区域已迁入双地址组件，须显式传入 Fiskaly/预览禁用条件
                                    disabledTaxTerritory={disabledTaxInput}
                                    // 隧道下载空城市时由 validateCityRequired 驱动营业城市必填态
                                    cityRequired={useData.validateCityRequired}
                                />
                            ),
                        }
                        break;
                    case 'contact_in_store_heading':
                    case 'contact_accountant_heading':
                        extraConfig = {
                            customSubChildren: (
                                <Typography
                                    className="restaurant-archive-subheading"
                                    variant="subtitle2"
                                >
                                    {i18n.t(childrenItem.label || '')}
                                </Typography>
                            ),
                        }
                        break;
                    case 'customer_mark':
                        extraConfig = {
                            customSubChildren: (
                                <RestaurantCollapsibleField
                                    label="archive_form_restaurant_remark_fold"
                                    value={useData.editRestaurantData.customer_mark || ''}
                                >
                                    <CommonInput
                                        inputType="INPUT"
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label="restaurant_info_page_text_customer_marks"
                                        size="small"
                                        disabled={useData.componentType === 'PREVIEW'}
                                        value={useData.editRestaurantData.customer_mark || ''}
                                        onChange={(event) => {
                                            useData.updateEditRestaurantData(event.target.value, 'customer_mark');
                                        }}
                                    />
                                </RestaurantCollapsibleField>
                            ),
                        }
                        break;
                    case 'city':
                        extraConfig = {
                            // 校验城市必填  用于餐厅隧道下载时的校验
                            required: useData.validateCityRequired ?? undefined,
                            regularType: useData.validateCityRequired ? 'required' : undefined,
                            disabledInput: useData.componentType === 'PREVIEW',
                            inputIsEdited: useData.operateMap.get(childrenItem.key) !== undefined ? true : false,
                        }
                        break;
                    case 'setting_restaurant_operating_status':
                    case 'ex_string_id':
                        extraConfig = {
                            // 新增模式下隐藏
                            hidden: useData.componentType === 'ADD',
                            // 预览模式下禁用
                            disabledInput: useData.componentType === 'PREVIEW',
                            inputIsEdited: useData.operateMap.get(childrenItem.key) !== undefined ? true : false,
                        }
                        break;
                    // 以下同为输入框  所以根据是否为预览模式加以禁用
                    case 'nickname':
                    case 'home_umber':
                    case 'postcode':
                    case 'state':
                    case 'country':
                    case 'reg_country':
                    case 'Aruba_username':
                    case 'Aruba_password':
                    case 'activation_time':
                    case 'plateform_api_user':
                    case 'plateform_api_password':
                    case 'accountant_name':
                    case 'accountant_phone':
                    case 'accountant_phone_prefix':
                    case 'reg_additional_address':
                    case 'restaurant_contact_person':
                    case 'email':
                    case 'tel_prefix':
                    case 'tel':
                    case 'website':
                    case 'discount_info':
                    case 'discount_annual':
                        extraConfig = {
                            disabledInput: useData.componentType === 'PREVIEW',
                            inputIsEdited: useData.operateMap.get(childrenItem.key) !== undefined ? true : false,
                        }
                        break;
                    case 'reg_state':
                        extraConfig = {
                            disabledInput: useData.componentType === 'PREVIEW',
                            inputIsEdited: useData.operateMap.get(childrenItem.key) !== undefined ? true : false,
                            // 仅税务省：法国 SIGN FR 邮箱模式；其它国家沿用 regName
                            regularType: isFrance ? 'franceFiskalyStateOptional' : 'regName',
                        }
                        break;
                    case 'local':
                        extraConfig = {
                            // 根据当前选择的国家
                            language: useData.getLanguage(),
                            disabledInput: useData.componentType === 'PREVIEW',
                            inputIsEdited: useData.operateMap.get(childrenItem.key) !== undefined ? true : false,
                        }
                        break;
                    case 'reg_des_code':
                        extraConfig = {
                            // 国家选择非意大利的时候， 电子发票收件人编码不显示
                            hidden: !isItaly,
                            disabledInput: useData.componentType === 'PREVIEW',
                            inputIsEdited: useData.operateMap.get(childrenItem.key) !== undefined ? true : false,
                        }
                        break;
                    case 'reg_city':
                    case 'reg_local':
                        extraConfig = {
                            // 不同的国家使用不同的正则
                            regularType: COUNTRY_REG_CITY_REGULAR[useData.editRestaurantData.country_code || ''],
                            disabledInput: useData.componentType === 'PREVIEW',
                            inputIsEdited: useData.operateMap.get(childrenItem.key) !== undefined ? true : false,
                            // 西班牙城市长度限制
                            inputProps: isSpain ? {
                                minLength: 1,
                                maxLength: 120
                            } : undefined
                        }
                        break;
                    case 'reg_postcode':
                        extraConfig = {
                            // 不同的国家使用不同的正则
                            regularType: COUNTRY_REG_POSTCODE_REGULAR[useData.editRestaurantData.country_code || ''],
                            disabledInput: useData.componentType === 'PREVIEW',
                            inputIsEdited: useData.operateMap.get(childrenItem.key) !== undefined ? true : false,
                            // 西班牙邮编长度限制
                            inputProps: isSpain ? {
                                minLength: 1,
                                maxLength: 20
                            } : undefined,
                        }
                        break;
                    case 'reg_municipality':
                        extraConfig = {
                            InputProps: {
                                endAdornment: <InputAdornment position="start">
                                    <ButtonComponent
                                        variant='text'
                                        handleClick={() => useData.searchMunicipalityByPostcode('reg_postcode', 'reg_municipality')}
                                        titleText={i18n.t('restaurant_info_page_text_search_municipality_by_postcode')}
                                        sx={{ marginRight: -6 }}
                                    >
                                        <SearchIcon />
                                    </ButtonComponent>
                                </InputAdornment>,
                            },
                            hidden: !isSpain,
                            disabledInput: useData.componentType === 'PREVIEW',
                            inputIsEdited: useData.operateMap.get(childrenItem.key) !== undefined ? true : false,
                        }
                        break;
                    case 'corp_municipality':
                        extraConfig = {
                            InputProps: {
                                endAdornment: <InputAdornment position="start">
                                    <ButtonComponent
                                        variant='text'
                                        handleClick={() => useData.searchMunicipalityByPostcode('corp_postcode', 'corp_municipality')}
                                        titleText={i18n.t('restaurant_info_page_text_search_municipality_by_postcode')}
                                        sx={{ marginRight: -6 }}
                                    >
                                        <SearchIcon />
                                    </ButtonComponent>
                                </InputAdornment>,
                            },
                            hidden: !isSpain,
                            disabledInput: useData.isDisabledLegalPerson || useData.componentType === 'PREVIEW',
                            inputIsEdited: useData.operateMap.get(childrenItem.key) !== undefined ? true : false,
                        }
                        break;
                    case 'reg_street_number':
                        extraConfig = {
                            // 西班牙 / 法国开店均需门牌号
                            hidden: !(isSpain || isFrance),
                            disabledInput: useData.componentType === 'PREVIEW',
                            inputIsEdited: useData.operateMap.get(childrenItem.key) !== undefined ? true : false,
                            // 法国用 SIGN FR 门牌规则（空不拦）；西班牙沿用既有正则
                            regularType: isFrance ? 'franceFiskalyAddress20' : 'regNameSpain20',
                            inputProps: (isSpain || isFrance) ? {
                                minLength: 1,
                                maxLength: 20,
                            } : undefined,
                        }
                        break;
                    case 'corp_street_number':
                        extraConfig = {
                            hidden: !isSpain,
                            disabledInput: useData.isDisabledLegalPerson || useData.componentType === 'PREVIEW',
                            inputIsEdited: useData.operateMap.get(childrenItem.key) !== undefined ? true : false,
                        }
                        break;
                    case 'reg_name':
                        extraConfig = {
                            disabledInput: disabledTaxInput,
                            inputIsEdited: useData.operateMap.get(childrenItem.key) !== undefined ? true : false,
                            // 不同的国家使用不同的正则
                            regularType: COUNTRY_REG_NAME_REGULAR[useData.editRestaurantData.country_code || ''],
                            // 西班牙长度限制
                            inputProps: isSpain ? {
                                minLength: 3,
                                maxLength: 120
                            } : undefined,
                        }
                        break;
                    case 'reg_territory':
                        // 公司名称、区域，如果注册了Fiskaly就不能修改了
                        extraConfig = {
                            hidden: !isSpain,
                            disabledInput: disabledTaxInput,
                            inputIsEdited: useData.operateMap.get(childrenItem.key) !== undefined ? true : false,
                        }
                        break;
                    case 'database_host':
                    case 'database_port':
                        extraConfig = {
                            // 经销商端不能编辑餐厅隧道信息配置
                            disabledInput: useData.componentType === 'PREVIEW' || accountState.accountType === 'RESELLER',
                            inputIsEdited: useData.operateMap.get(childrenItem.key) !== undefined ? true : false,
                            hidden: childrenItem.key === 'database_port' ? useData.isMultiDatabasePortMode : false,
                        }
                        break;
                    case 'database_port_array':
                        extraConfig = {
                            hidden: !useData.isMultiDatabasePortMode,
                            customSubChildren: renderMultiDatabasePortInputs()
                        }
                        break;
                    case 'qop_efr_url_display':
                        extraConfig = {
                            hidden: !useData.QOP_WAN_WARRANT || !useData.editRestaurantData.string_id,
                            customSubChildren: (
                                <Box sx={{ width: '100%' }}>
                                    <CommonInput
                                        inputType="INPUT"
                                        fullWidth
                                        label='EFR'
                                        size="small"
                                        disabled
                                        value={getQopEfrTunnelHost(useData.editRestaurantData.string_id || '')}
                                        onChange={() => undefined}
                                    />
                                </Box>
                            )
                        };
                        break;
                    case 'belonged_group':
                        extraConfig = {
                            disabledInput: useData.componentType === 'PREVIEW',
                            inputIsEdited: useData.operateMap.get(`belonged_group`) !== undefined ? true : false,
                            items: useData.groupAllData?.map(n => ({ value: n.string_id as string, label: n.nickname as string })),
                        }
                        break;
                    case 'time_zone':
                        extraConfig = {
                            disabledInput: useData.componentType === 'PREVIEW',
                            inputIsEdited: useData.operateMap.get(`time_zone`) !== undefined ? true : false,
                            items: useData.countryTimeZone,
                        }
                        break;
                    case 'country_code':
                        extraConfig = {
                            // 国家决定税号与发票规则，餐厅创建后不可修改。
                            disabledInput: useData.componentType !== 'ADD',
                            inputIsEdited: useData.operateMap.get(`country_code`) !== undefined ? true : false,
                            items: useData.countryCodeList || [],
                        }
                        break;
                    case 'restaurant_version':
                        extraConfig = {
                            disabledInput: useData.componentType === 'PREVIEW',
                            inputIsEdited: useData.operateMap.get(`restaurant_version`) !== undefined ? true : false,
                            items: useData.restaurantVersions,
                        }
                        break;
                    case 'business_labels':
                        extraConfig = {
                            disabledInput: useData.componentType === 'PREVIEW',
                            inputIsEdited: useData.operateMap.get('business_labels') !== undefined,
                            multipleProps: {
                                options: useData.businessLabels,
                            },
                        }
                        break;
                    case 'belonged_reseller':
                        extraConfig = {
                            // 经销商账号不可改所属经销商（与隧道/GYB 限制一致）
                            disabledInput: isBelongedResellerInputDisabled(
                                useData.componentType,
                                accountState.accountType,
                            ),
                            inputIsEdited: useData.operateMap.get('belonged_reseller') !== undefined,
                            items: useData.resellerList,
                        }
                        break;
                    case 'authorization_type':
                        extraConfig = {
                            disabledInput: useData.componentType === 'PREVIEW',
                            inputIsEdited: useData.operateMap.get('authorization_type') !== undefined,
                            items: useData.authorizationTypes,
                        }
                        break;
                    case 'head':
                        extraConfig = {
                            // 经销商不能编辑餐厅隧道信息配置
                            disabledInput: useData.componentType === 'PREVIEW' || accountState.accountType === 'RESELLER',
                            inputIsEdited: useData.operateMap.get(`head`) !== undefined ? true : false,
                            items: [
                                { value: 'http', label: 'http' },
                                { value: 'https', label: 'https' }
                            ],
                            hidden: !useData.QOP_WAN_WARRANT
                        }
                        break;
                    case 'url':
                    case 'port':
                        extraConfig = {
                            // 经销商不能编辑餐厅隧道信息配置
                            disabledInput: useData.componentType === 'PREVIEW' || accountState.accountType === 'RESELLER',
                            inputIsEdited: useData.operateMap.get(childrenItem.key) !== undefined ? true : false,
                            hidden: !useData.QOP_WAN_WARRANT
                        };
                        break;
                    case 'staff_info_list':
                        extraConfig = {
                            tableConfig: {
                                showChecked: 0,
                                headCellList: [
                                    { id: 'nickname', label: i18n.t('general_table_text_staff_name'), sortStatus: -1 },
                                    { id: 'email', label: i18n.t('general_text_email'), sortStatus: -1 },
                                    {
                                        id: 'phone', label: i18n.t('restaurant_info_page_table_text_staff_phone'), sortStatus: -1,
                                        renderFunction: (row: any) => {
                                            return `${row.phone_prefix || ''} ${row.phone || ''}`
                                        }
                                    },
                                    {
                                        id: 'staff_belonged_group.nickname', label: i18n.t('general_text_belonged_group'), sortStatus: -1,
                                        renderFunction: (row: Staff) => {
                                            return `${row?.staff_belonged_group?.nickname || ''}`
                                        }
                                    },
                                    {
                                        id: 'staff_belonged_group.string_id', label: i18n.t('group_management_page_text_group_identification_code'),
                                        renderFunction: (row: Staff) => {
                                            return <>{row?.staff_belonged_group?.string_id || ''}</>
                                        }
                                    },
                                    {
                                        id: '-1', label: '', sortStatus: -1,
                                        renderFunction: (row: Staff) => {
                                            return useData.componentType !== 'PREVIEW' ? staffInfoListRenderFeatures(row) : <></>
                                        }
                                    }
                                ],
                                list: JSON.stringify(useData.restaurantInfo?.owned_staff?.[0]) !== '{}' ? useData?.restaurantInfo?.owned_staff || [] : [],
                            }
                        }
                        break;
                    case 'extensions_list':
                        extraConfig = {
                            tableConfig: {
                                showChecked: 0,
                                headCellList: [
                                    { id: 'name', label: i18n.t('responsible_person_info_component_text_name'), sortStatus: -1 },
                                    {
                                        id: 'value', label: i18n.t('restaurant_extensions_page_text_key'), sortStatus: -1,
                                        renderFunction: (row: any) => {
                                            return (<Box className='FC' style={{ wordBreak: 'break-all', minWidth: '`100px' }} >
                                                <ButtonComponent
                                                    variant='text'
                                                    handleClick={() => {
                                                        const res = copy(row?.value);
                                                        if (res) GlobalNotification.success(i18n.t('restaurant_page_input_tip_message_copy_successfully'))
                                                    }}>
                                                    <ContentCopy />
                                                </ButtonComponent>
                                                {row?.value}
                                            </Box>)
                                        }
                                    },
                                    {
                                        id: 'creation_time', label: i18n.t('restaurant_extensions_page_text_create_time'), sortStatus: -1,
                                        renderFunction: (row: any) => {
                                            return row?.creation_time ? moment(row.creation_time, moment.HTML5_FMT.DATETIME_LOCAL_MS).format('HH:mm:ss DD/MM/YYYY') : '';
                                        }
                                    },
                                    {
                                        id: '-1', label: '', sortStatus: -1,
                                        renderFunction: (row: SecretKey) => {
                                            return useData.componentType !== 'PREVIEW' ? extensionsRenderFeatures(row) : <></>
                                        }
                                    }
                                ],
                                list: useData?.restaurantInfo?.secret_key || [],
                            }
                        }
                        break;
                    case 'gyb_info_list':
                        extraConfig = {
                            tableConfig: {
                                showChecked: 0,
                                headCellList:
                                    // 经销商无法操作gyb
                                    useData.componentType === 'PREVIEW' || accountState.accountType === 'RESELLER' ?
                                        [
                                            { id: 'location_id', label: i18n.t('gyb_page_text_registered_id'), sortStatus: -1 },
                                            { id: 'operator', label: i18n.t('gyb_page_text_operator'), sortStatus: -1 },
                                            { id: 'license_code', label: i18n.t('gyb_page_text_GYB_key'), sortStatus: -1 },
                                            { id: 'device_code', label: i18n.t('gyb_page_text_the_device_number'), sortStatus: -1 },

                                        ]
                                        :
                                        [
                                            { id: 'location_id', label: i18n.t('gyb_page_text_registered_id'), sortStatus: -1 },
                                            { id: 'operator', label: i18n.t('gyb_page_text_operator'), sortStatus: -1 },
                                            { id: 'license_code', label: i18n.t('gyb_page_text_GYB_key'), sortStatus: -1 },
                                            { id: 'device_code', label: i18n.t('gyb_page_text_the_device_number'), sortStatus: -1 },
                                            {
                                                id: 'is_default', label: i18n.t('gyb_page_text_default_device'), sortStatus: -1,
                                                renderFunction: (row: any,) => {
                                                    return row.is_default === 1 ? i18n.t('general_text_yes') : i18n.t('general_text_no')
                                                }
                                            },
                                            {
                                                id: '-1', label: '', sortStatus: -1,
                                                renderFunction: (row: any) => {
                                                    return gybInfoRenderFeatures(row)
                                                }
                                            }

                                        ],
                                list: useData?.GYBList || [],
                            }
                        }
                        break;
                    case 'core_file_list':
                        extraConfig = {
                            tableConfig: {
                                showChecked: 0,
                                headCellList: [
                                    {
                                        id: 'date', label: i18n.t('core_file_component_date'), sortStatus: -1,
                                        renderFunction: (row: any) => {
                                            return `${moment(row.date, moment.HTML5_FMT.DATETIME_LOCAL_MS).format('YYYY-MM-DD HH:mm:ss')}`
                                        }
                                    },
                                    {
                                        id: 'operatorFirstName', label: i18n.t('core_file_component_operator_name'), sortStatus: -1,
                                        renderFunction: (row: any) => {
                                            return `${row.operatorFirstName || ''} ${row.operatorLastName || ''}`
                                        }
                                    },
                                    { id: 'operatorType', label: i18n.t('core_file_component_operator_type'), sortStatus: -1 },
                                    {
                                        id: '-1', label: '', sortStatus: -1,
                                        renderFunction: (row: any) => {
                                            return coreFileLogRenderFeatures(row)
                                        }
                                    }
                                ],
                                list: useData?.coreFileLog || [],
                                isPagination: true,
                                allCount: useData?.coreFileLog?.length || 0,
                                isUseCustomPagination: true,
                            }
                        }
                        break;
                    case 'reg_fiscal_code':
                        extraConfig = {
                            disabledInput: useData.componentType === 'PREVIEW',
                            inputIsEdited: useData.operateMap.get(childrenItem.key) !== undefined ? true : false,
                            // 非意大利国家忽略必填校验
                            required: isItaly ? childrenItem.required : false,
                            // 意大利国家才校验
                            regularType: isItaly ? 'vatNumber' : undefined,
                        }
                        break;
                    case 'reg_vat_number': {
                        // 有 OCR 置信度标签时隐藏「同税务注册号」，避免与标签重叠（对齐原型 twin&&!aiInfo）
                        const showSameFiscalCodeButton = !useData.ocrSession?.fields.some(
                            (field) => field.fieldId === 'reg_vat_number',
                        );
                        extraConfig = {
                            disabledInput: disabledTaxInput,
                            inputIsEdited: useData.operateMap.get(childrenItem.key) !== undefined ? true : false,
                            // 非意大利国家忽略必填校验
                            required: (isItaly || isSpain) ? true : false,
                            // 不同的国家使用不同的正则
                            regularType: COUNTRY_VAT_REGULAR[useData.editRestaurantData.country_code || ''],
                            InputProps: showSameFiscalCodeButton ? {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Button
                                            variant="text"
                                            disabled={disabledTaxInput}
                                            sx={{ whiteSpace: 'nowrap' }}
                                            onClick={() => {
                                                const fiscalCode = useData.editRestaurantData.reg_fiscal_code || '';
                                                // 意/西取对应 VAT 规则；其它国家无正则 → null（不校验格式，避免回落 fiscalCode）
                                                const vatRegularType = COUNTRY_VAT_REGULAR[
                                                    useData.editRestaurantData.country_code || ''
                                                ] ?? null;
                                                if (!isFiscalCodeValidForCopy(fiscalCode, vatRegularType)) {
                                                    GlobalNotification.error(i18n.t('archive_form_group_same_fiscal_code_invalid'));
                                                    return;
                                                }
                                                useData.updateEditRestaurantData(
                                                    fiscalCode,
                                                    'reg_vat_number',
                                                );
                                            }}
                                        >
                                            {i18n.t('archive_form_group_same_fiscal_code')}
                                        </Button>
                                    </InputAdornment>
                                ),
                            } : undefined,
                        }
                        break;
                    }
                    case 'corp_name':
                        extraConfig = {
                            disabledInput: useData.isDisabledLegalPerson || useData.componentType === 'PREVIEW',
                            inputIsEdited: useData.operateMap.get(childrenItem.key) !== undefined ? true : false,
                            // 不同的国家使用不同的正则
                            regularType: COUNTRY_REG_NAME_REGULAR[useData.editRestaurantData.country_code || ''],
                            // 西班牙长度限制
                            inputProps: isSpain ? {
                                minLength: 3,
                                maxLength: 120
                            } : undefined,
                        }
                        break;
                    case 'corp_vat_number':
                        extraConfig = {
                            disabledInput: useData.isDisabledLegalPerson || useData.componentType === 'PREVIEW',
                            inputIsEdited: useData.operateMap.get(childrenItem.key) !== undefined ? true : false,
                            // 不同的国家使用不同的正则
                            regularType: COUNTRY_VAT_REGULAR[useData.editRestaurantData.country_code || ''],
                        }
                        break;
                    case 'corp_local':
                        extraConfig = {
                            disabledInput: useData.isDisabledLegalPerson || useData.componentType === 'PREVIEW',
                            inputIsEdited: useData.operateMap.get(childrenItem.key) !== undefined ? true : false,
                            // 不同的国家使用不同的正则
                            regularType: COUNTRY_REG_CITY_REGULAR[useData.editRestaurantData.country_code || ''],
                        }
                        break;
                    case 'corp_additional_address':
                        extraConfig = {
                            disabledInput: useData.isDisabledLegalPerson || useData.componentType === 'PREVIEW',
                            inputIsEdited: useData.operateMap.get(childrenItem.key) !== undefined ? true : false,
                        }
                        break;
                    case 'corp_postcode':
                        extraConfig = {
                            disabledInput: useData.isDisabledLegalPerson || useData.componentType === 'PREVIEW',
                            inputIsEdited: useData.operateMap.get(childrenItem.key) !== undefined ? true : false,
                            // 不同的国家使用不同的正则
                            regularType: COUNTRY_REG_POSTCODE_REGULAR[useData.editRestaurantData.country_code || ''],
                        }
                        break;
                    case 'corp_city':
                        extraConfig = {
                            disabledInput: useData.isDisabledLegalPerson || useData.componentType === 'PREVIEW',
                            inputIsEdited: useData.operateMap.get(childrenItem.key) !== undefined ? true : false,
                            // 不同的国家使用不同的正则
                            regularType: COUNTRY_REG_CITY_REGULAR[useData.editRestaurantData.country_code || ''],
                        }
                        break;
                    case 'corp_state':
                        extraConfig = {
                            disabledInput: useData.isDisabledLegalPerson || useData.componentType === 'PREVIEW',
                            inputIsEdited: useData.operateMap.get(childrenItem.key) !== undefined ? true : false,
                        }
                        break;
                    case 'corp_country':
                        extraConfig = {
                            disabledInput: useData.isDisabledLegalPerson || useData.componentType === 'PREVIEW',
                            inputIsEdited: useData.operateMap.get(childrenItem.key) !== undefined ? true : false,
                        }
                        break;
                    case 'reg_pec':
                        extraConfig = {
                            disabledInput: useData.componentType === 'PREVIEW',
                            inputIsEdited: useData.operateMap.get(childrenItem.key) !== undefined ? true : false,
                            // 非意大利国家忽略必填校验
                            required: isItaly ? childrenItem.required : false,
                        }
                        break;
                    case 'fiskaly_manage_organization_user_id':
                        extraConfig = {
                            customSubChildren: (
                                <Box sx={{ mt: -2 }}>
                                    <AsyncCheckboxWithLoading
                                        disabled={useData.componentType === 'PREVIEW'}
                                        checked={!!useData.restaurantInfo.fiskaly_manage_organization_user_id}
                                        label={(
                                            <Typography variant='body2'>
                                                {i18n.t('restaurant_info_page_text_fiskaly_manage_organization_user_id')}
                                            </Typography>
                                        )}
                                        onChange={async (val) => {
                                            await useData.inviteFiskalyChange(val);
                                        }}
                                    />
                                </Box>
                            )
                        }
                        break;
                    case 'fiskaly_client_info':
                        extraConfig = {
                            tableConfig: {
                                showChecked: 0,
                                headCellList: [
                                    { id: 'fiskaly_client_id', label: i18n.t('restaurant_info_page_table_text_fiskaly_client_id') },
                                    { id: 'fiskaly_invoice_series', label: i18n.t('restaurant_info_page_table_text_invoice_series') },
                                    { id: 'pos_name', label: i18n.t('restaurant_info_page_table_text_pos_name') },
                                    {
                                        id: 'status', label: i18n.t('restaurant_info_page_table_text_fiskaly_client_status'),
                                        renderFunction: (row) => {
                                            return row?.status === 'ENABLED' ?
                                                <Button
                                                    color='success'
                                                    variant='contained'
                                                    onClick={() => useData.updateConfirmDialog({
                                                        visible: true,
                                                        title: '',
                                                        content: i18n.t('restaurant_info_page_button_unbind_fiskaly_client'),
                                                        handleClick: async () => await useData.unbindDisableFiskalyClientInfo(row, 'DISABLED')
                                                    })}
                                                    sx={{ color: '#fff' }}
                                                >
                                                    {i18n.t('restaurant_info_page_text_status_enabled')}
                                                </Button>
                                                :
                                                <Button
                                                    disabled
                                                    color='error'
                                                    variant='contained'
                                                >
                                                    {i18n.t('restaurant_info_page_text_status_disabled')}
                                                </Button>
                                        }
                                    },
                                    { id: 'workstations_id', label: i18n.t('restaurant_info_page_table_text_workstation_id') },
                                    {
                                        id: '-1', label: '', sortStatus: -1,
                                        renderFunction: (row: FiskalyClientInfo) => row.status === 'DISABLED' ? fiskalyClientInfoRenderFeatures(row) : null
                                    }
                                ],
                                list: JSON.stringify(useData.restaurantInfo?.fiskaly_client_info?.[0]) !== '{}' ? useData?.restaurantInfo?.fiskaly_client_info || [] : [],
                            }
                        }
                        break;
                    case 'fiskaly_sign_info': {
                        // 个人税号时展示可编辑 activity_code 列（非必填，label 不走 i18n）
                        const showActivityCode = shouldShowFiskalyActivityCodeColumn(
                            useData.restaurantInfo?.vat_number_is_personal,
                        );
                        // 表头列：签名信息 + 条件列 activity_code
                        const fiskalySignHeadCells = [
                            {
                                id: 'fiskaly_signer_id', label: 'Signer ID', renderFunction: (row: {
                                    fiskaly_signer_id?: string;
                                }) => {
                                    return row?.fiskaly_signer_id ? <Box className='FC' style={{ wordBreak: 'break-all' }} >
                                        <ButtonComponent
                                            variant='text'
                                            handleClick={() => {
                                                const res = copy(row.fiskaly_signer_id || '');
                                                if (res) GlobalNotification.success(i18n.t('restaurant_page_input_tip_message_copy_successfully'))
                                            }}>
                                            <ContentCopy />
                                        </ButtonComponent>
                                        {row?.fiskaly_signer_id}
                                    </Box> : null
                                }
                            },
                            {
                                id: 'fiskaly_serial_number', label: 'Serial No.', renderFunction: (row: {
                                    fiskaly_serial_number?: string;
                                }) => {
                                    return row?.fiskaly_serial_number ? <Box className='FC' style={{ wordBreak: 'break-all' }} >
                                        <ButtonComponent
                                            variant='text'
                                            handleClick={() => {
                                                const res = copy(row.fiskaly_serial_number || '');
                                                if (res) GlobalNotification.success(i18n.t('restaurant_page_input_tip_message_copy_successfully'))
                                            }}>
                                            <ContentCopy />
                                        </ButtonComponent>
                                        {row?.fiskaly_serial_number}
                                    </Box> : null
                                }
                            },
                            ...(showActivityCode
                                ? [{
                                    id: 'fiskaly_activity_code',
                                    label: 'activity_code',
                                    // 单元格内可编辑，绑定餐厅表单 fiskaly_activity_code
                                    renderFunction: () => (
                                        <CommonInput
                                            inputType="INPUT"
                                            fullWidth
                                            size="small"
                                            disabled={useData.componentType === 'PREVIEW'}
                                            value={useData.editRestaurantData?.fiskaly_activity_code || ''}
                                            onChange={(e) => {
                                                useData.updateEditRestaurantData(
                                                    e.target.value,
                                                    'fiskaly_activity_code',
                                                );
                                            }}
                                        />
                                    ),
                                }]
                                : []),
                            {
                                id: 'fiskaly_signer_status', label: i18n.t('restaurant_info_page_status'), renderFunction: (row: {
                                    fiskaly_signer_status?: string;
                                }) => {
                                    return <>
                                        {row?.fiskaly_signer_status === 'ENABLED' ?
                                            <Button
                                                color='success'
                                                variant='contained'
                                                sx={{ color: '#fff' }}
                                            >
                                                {i18n.t('restaurant_info_page_text_status_enabled')}
                                            </Button>
                                            :
                                            null

                                        }
                                        {
                                            row?.fiskaly_signer_status === 'DISABLED' ?
                                                <Button
                                                    disabled
                                                    color='error'
                                                    variant='contained'
                                                >
                                                    {i18n.t('restaurant_info_page_text_status_disabled')}
                                                </Button>
                                                :
                                                null
                                        }
                                    </>
                                }
                            }
                        ];
                        extraConfig = {
                            tableConfig: {
                                showChecked: 0,
                                headCellList: fiskalySignHeadCells,
                                // 有签名信息，或个人税号需编辑 activity_code 时至少展示一行
                                list: shouldShowFiskalySignInfoList(
                                    hasFiskalyInfo,
                                    useData.restaurantInfo?.vat_number_is_personal,
                                )
                                    ? [
                                        {
                                            fiskaly_signer_id,
                                            fiskaly_serial_number,
                                            fiskaly_signer_status,
                                            fiskaly_activity_code:
                                                useData.editRestaurantData?.fiskaly_activity_code || '',
                                        },
                                    ]
                                    : []
                            }
                        }
                        break;
                    }

                    default:
                        break;
                }
                // 双隧道模式下，数据库主机字段占满一行
                let tunnelLayoutMd = childrenItem.md;
                if (useData.isMultiDatabasePortMode && childrenItem.key === 'database_host') {
                    tunnelLayoutMd = 12;
                }
                const tunnelLayoutConfig = { md: tunnelLayoutMd };
                // 允许空校验
                const allowNullVerification = useData.allowNullVerification
                return {
                    ...childrenItem,
                    ...extraConfig,
                    ...tunnelLayoutConfig,
                    isEmpty: allowNullVerification,
                    onChange: async (e) => {
                        if (childrenItem.key === 'country_code') {
                            useData.updateRestaurantCountryCode(e.target.value);
                        } else {
                            useData.updateEditRestaurantData(e.target.value, childrenItem.key);
                        }
                        // 核心文件日志 2.5餐厅
                        if (useData.componentType !== 'ADD' && 'restaurant_version' && e.target.value === '2.5') {
                            await useData.getCoreFileLog();
                        }
                    },
                    handleMultipleChange: (value) => {
                        return useData.updateEditRestaurantData(value, childrenItem.key);
                    },
                    handleChangeCountry: (value) => {
                        return useData.updateEditRestaurantData(value, childrenItem.key);
                    },
                    handleChangePicker: (value) => {
                        const pickerValue = value ? moment(value).format(moment.HTML5_FMT.DATETIME_LOCAL_MS) : null;
                        useData.updateEditRestaurantData(pickerValue || '', childrenItem.key);
                    },
                    handleChangeGooglePlace: (val) => {
                        // 更新地址、邮编、国家、城市、省份
                        useData.updateEditRestaurantData(val.value || '', childrenItem.key);
                        useData.updateEditRestaurantData(val.postalCode || '', 'postcode');
                        useData.updateEditRestaurantData(val.country || '', 'country');
                        useData.updateEditRestaurantData(val.city || '', 'city');
                        useData.updateEditRestaurantData(val.state || '', 'state');
                    },
                }

            }),
            subCardConfig: item?.subCardConfig?.map((subCardItm) => {
                subCardItm.children = subCardItm.children?.map((childrenItem) => {
                    let extraConfig = {};
                    switch (childrenItem.key) {
                        case 'email':
                        case 'tel_prefix':
                        case 'tel':
                        case 'restaurant_contact_person':
                        case 'website':
                        case 'discount_info':
                        case 'discount_annual':
                        case 'customer_mark':
                            extraConfig = {
                                disabledInput: useData.componentType === 'PREVIEW',
                                inputIsEdited: useData.operateMap.get(childrenItem.key) !== undefined ? true : false,
                            }
                            break;
                        case 'business_labels': {
                            extraConfig = {
                                disabledInput: useData.componentType === 'PREVIEW',
                                inputIsEdited: useData.operateMap.get(`business_labels`) !== undefined ? true : false,
                                multipleProps: {
                                    options: useData.businessLabels,
                                },
                            }
                            break;
                        }
                        case 'belonged_reseller': {
                            extraConfig = {
                                // 经销商账号不可改所属经销商（与隧道/GYB 限制一致）
                                disabledInput: isBelongedResellerInputDisabled(
                                    useData.componentType,
                                    accountState.accountType,
                                ),
                                inputIsEdited: useData.operateMap.get(`belonged_reseller`) !== undefined ? true : false,
                                items: useData.resellerList,
                            }
                            break;
                        }
                        case 'authorization_type':
                            extraConfig = {
                                disabledInput: useData.componentType === 'PREVIEW',
                                inputIsEdited: useData.operateMap.get(`authorization_type`) !== undefined ? true : false,
                                items: useData.authorizationTypes,
                            }
                            break;
                        default:
                            break;
                    }
                    const allowNullVerification = useData.allowNullVerification
                    return {
                        ...childrenItem,
                        ...extraConfig,
                        isEmpty: allowNullVerification,
                        onChange: async (e) => {
                            useData.updateEditRestaurantData(e.target.value, childrenItem.key);
                            // 核心文件日志 2.5餐厅
                            if (useData.componentType !== 'ADD' && 'restaurant_version' && e.target.value === '2.5') {
                                await useData.getCoreFileLog();
                            }
                        },
                        handleMultipleChange: (value) => {
                            return useData.updateEditRestaurantData(value, childrenItem.key);
                        },
                        handleChangeCountry: (value) => {
                            return useData.updateEditRestaurantData(value, childrenItem.key);
                        }
                    }
                })
                return {
                    ...subCardItm
                }
            })
        }
    }) as CardFormConfig[];

    const data = { ...useData.editRestaurantData, avatar: useData.editRestaurantDataAvatar?.src }
    /** 扫描会话；手动填写时为空。 */
    const ocrSession = useData.ocrSession;
    /** 识别字段索引，供卡片输入框挂核对控件。 */
    const ocrFieldMap = Object.fromEntries((ocrSession?.fields ?? []).map((field) => [field.fieldId, field]));
    /** 已填充与已核对数量。 */
    const ocrCounts = countOcrReview(ocrSession);
    // 点左侧原件框时滚右侧表单；点表单/印章只更新对位、不滚表单（避免核对跳动）
    React.useEffect(() => {
        if (!useData.ocrFormScrollSeq || !ocrSession?.activeFieldId) {
            return undefined;
        }
        /** 右侧核对字段外框。 */
        const target = document.querySelector(`[data-ocr-field="${ocrSession.activeFieldId}"]`);
        if (target) {
            target.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
        return undefined;
    }, [useData.ocrFormScrollSeq, ocrSession?.activeFieldId]);
    /** 核心分区规则沿用餐厅弹窗底栏的现有完成度语义。 */
    const archiveSectionRules = getRestaurantArchiveSections({
        componentType: useData.componentType,
        countryCode: useData.editRestaurantData.country_code || '',
        isReseller: accountState.accountType === 'RESELLER',
        restaurantVersion: useData.editRestaurantData.restaurant_version || '',
    });
    /** 当前餐厅核心字段完成度，用于工作台分区角标。 */
    const archiveProgress = calculateArchiveFormProgress(
        archiveSectionRules,
        useData.editRestaurantData as Record<string, unknown>,
    );
    /** 不同信息来源对应的用户可见说明。 */
    const sourceLabels: Record<ArchiveFormSectionRule['source'], string> = {
        SALES: i18n.t('archive_form_source_sales'),
        DOCUMENT: i18n.t('create_group_component_text_source_visura'),
        INTERNAL: i18n.t('archive_form_source_internal'),
    };
    /**
     * 返回分区标题旁展示的信息来源文案。
     * @param sectionKey 分区标识
     * @param source 建档信息来源
     */
    const getSectionSourceLabel = (
        sectionKey: string,
        source?: ArchiveFormSectionRule['source'],
    ) => {
        if (!source) {
            return sourceLabels.INTERNAL;
        }
        if (sectionKey === 'tax-and-address') {
            return i18n.t('create_group_component_text_source_visura');
        }
        return sourceLabels[source];
    };
    /** 工作台覆盖四个核心分区及所有满足既有显示条件的扩展分区。 */
    const workbenchSections = cardConfig
        .filter((sectionConfig) => !sectionConfig.hidden)
        .map((sectionConfig) => {
            /** 核心分区使用既有来源和必填进度，扩展分区归为内部信息。 */
            const sectionRule = archiveSectionRules.find(
                (section) => section.key === sectionConfig.key,
            );
            /** 当前核心分区尚未完成的必填字段数。 */
            const sectionProgress = archiveProgress.sections.find(
                (section) => section.key === sectionConfig.key,
            );
            /** 分区标题 i18n key。 */
            const sectionTitleKey = typeof sectionConfig.title === 'string'
                ? sectionConfig.title
                : sectionConfig.key;
            /** 原型要求的核心分区提示文案。 */
            const sectionHintKey = RESTAURANT_ARCHIVE_SECTION_HINTS[sectionConfig.key];
            /** 仅「新增 + 扫描建档」才挂 OCR 分区标签，避免覆盖编辑态原有 actionRender。 */
            const isScanCreate = useData.componentType === 'ADD' && Boolean(ocrSession);
            /** 本分区可计入 OCR 的字段名（含双地址展开）。 */
            const sectionOcrKeys = isScanCreate
                ? collectRestaurantSectionOcrKeys(sectionConfig.children ?? [])
                : [];
            /** 扫描建档时本分区被 AI 写入的字段数。 */
            const sectionAiFillCount = isScanCreate && ocrSession
                ? countOcrFieldsForSectionKeys(ocrSession.fields, sectionOcrKeys)
                : 0;
            /** 注入提示行后的分区卡片配置。 */
            const enhancedSection: CardFormConfig = {
                ...sectionConfig,
                xs: 12,
                md: 12,
                rowSpacing: 3,
                columnSpacing: { xs: 3, md: 3 },
                title: sectionRule ? (
                    <Box component="span" className="restaurant-info-section-title">
                        <Typography component="span" variant="subtitle1">
                            {i18n.t(sectionTitleKey)}
                        </Typography>
                        <Typography component="span" variant="caption" color="text.secondary">
                            {getSectionSourceLabel(sectionConfig.key, sectionRule.source)}
                        </Typography>
                    </Box>
                ) : i18n.t(sectionTitleKey),
                // 扫描建档才替换右上角；隧道分区字段不靠 OCR，保留单/双隧道等原有按钮
                actionRender: isScanCreate && sectionConfig.key !== 'tunnel' ? (
                    <Typography
                        component="span"
                        variant="caption"
                        className={
                            sectionAiFillCount > 0
                                ? 'archive-form__section-tag archive-form__section-tag--ai'
                                : 'archive-form__section-tag archive-form__section-tag--manual'
                        }
                    >
                        {sectionAiFillCount > 0
                            ? i18n.t('archive_form_ocr_section_ai_fill', { count: sectionAiFillCount })
                            : i18n.t('archive_form_ocr_section_manual')}
                    </Typography>
                ) : sectionConfig.actionRender,
                children: sectionHintKey
                    ? [{
                        key: `${sectionConfig.key}_hint`,
                        comType: 'CUSTOMCHILDREN',
                        className: 'restaurant-info-section-hint-field',
                        xs: 12,
                        md: 12,
                        customSubChildren: (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                className="restaurant-info-section-hint"
                            >
                                {i18n.t(sectionHintKey)}
                            </Typography>
                        ),
                    }, ...(sectionConfig.children ?? [])]
                    : sectionConfig.children,
            };
            return {
                key: sectionConfig.key,
                title: i18n.t(sectionTitleKey),
                // 来源说明并入卡片标题，避免在卡片外重复占据一行。
                sourceLabel: '',
                pendingRequired: sectionProgress?.pendingRequired ?? 0,
                content: (
                    <CardFrom
                        config={[enhancedSection]}
                        data={data as {}}
                        googleMapsLoaded={googleMapsLoaded}
                        ocrFields={ocrFieldMap}
                        ocrActiveFieldId={ocrSession?.activeFieldId}
                        onOcrConfirm={(fieldId) => useData.confirmOcrField(fieldId)}
                        onOcrAdopt={(fieldId) => useData.adoptOcrField(fieldId)}
                        onOcrFocus={(fieldId) => useData.focusOcrField(fieldId)}
                    />
                ),
            };
        });
    return (
        <Box className="restaurant-info-page restaurant-info-page--dialog">
            {
                googleMapsApiKey &&
                <LoadGoogleMap
                    googleMapsApiKey={googleMapsApiKey}
                    onLoaded={(isLoaded) => {
                        setGoogleMapsLoaded(isLoaded === true);
                    }}
                />
            }
            {

                skeleton ?
                    <SkeletonOfRestaurantInfo
                        componentType={useData.componentType}
                        countryCode={useData.editRestaurantData.country_code || ''}
                    />
                    :
                    (
                        <ArchiveFormWorkbench
                            // OCR 提示与锚点占满一行；原件预览仅与表单分区并排
                            topContent={ocrSession ? (
                                <ArchiveOcrBanner
                                    total={ocrCounts.total}
                                    armed={ocrSession.confirmHighArmed}
                                    onToggle={() => useData.toggleOcrConfirmHigh()}
                                />
                            ) : null}
                            sidePanel={ocrSession ? (
                                <ArchiveOcrDocumentPane
                                    fileUrl={useData.ocrFileUrl}
                                    mimeType={useData.ocrFileMime}
                                    fileName={ocrSession.fileName}
                                    fields={ocrSession.fields}
                                    activeFieldId={ocrSession.activeFieldId}
                                    focusSeq={useData.ocrFocusSeq}
                                    onFocusField={(fieldId, options) => useData.focusOcrField(fieldId, options)}
                                />
                            ) : null}
                            sections={workbenchSections}
                            progress={archiveProgress}
                            actions={null}
                            showSaveProgress={false}
                        />
                    )
            }
        </Box>
    )
})